import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'

import { ApiError } from '@/api/errors'
import { PROSPECTING_KEY, prospectingApi } from '@/api/prospecting'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { ProgramAbTests } from '@/components/prospecting/program-ab-tests'
import { ProgramDelivery } from '@/components/prospecting/program-delivery'
import { ProgramGrid } from '@/components/prospecting/program-grid'
import { ProgramLanding } from '@/components/prospecting/program-landing'
import { ProgramMeta } from '@/components/prospecting/program-meta'
import { ProgramReport } from '@/components/prospecting/program-report'
import { ProgramSettings } from '@/components/prospecting/program-settings'
import { ProgramStopRules } from '@/components/prospecting/program-stop-rules'
import { ProspectTierBadge } from '@/components/prospecting/prospect-tier-badge'
import { ReferencedBy } from '@/components/references/referenced-by'
import { ReturnLink } from '@/components/return-link'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { authorName } from '@/lib/people'
import { PROSPECT_TIERS } from '@/lib/prospecting'
import { RETURN_SHAPE } from '@/lib/return-to'
import { oneOf, readSearch } from '@/lib/search-params'

const TABS = [
  'grille',
  'meta',
  'diffusion',
  'tests',
  'regles',
  'page',
  'rapport',
  'reglages',
] as const

export const Route = createFileRoute('/_app/prospecting/programs/$programId')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch({ onglet: oneOf(TABS), ...RETURN_SHAPE }, search),
  component: ProgramPage,
})

/**
 * Un programme de prospection (lot 43, contrat 1.86.0) : sa grille et ses
 * seuils, Meta (formulaires, correspondances, campagnes), la diffusion
 * programmée, les tests A/B et les règles d'arrêt (lot 44, 1.87.0), la page
 * de qualification, le rapport et ses graphiques, et ses réglages. L'onglet
 * vit dans l'adresse.
 */
function ProgramPage() {
  const { programId } = Route.useParams()
  const { onglet } = Route.useSearch()
  const navigate = Route.useNavigate()
  const program = useQuery({
    queryKey: [...PROSPECTING_KEY, 'program', programId],
    queryFn: () => prospectingApi.program(programId),
    retry: false,
  })
  if (program.isPending) {
    return (
      <Frame>
        <Skeleton className="h-64 w-full" />
      </Frame>
    )
  }
  if (program.isError) {
    return (
      <Frame>
        <FormError>
          {program.error instanceof ApiError && program.error.isNotFound
            ? 'Ce programme n’existe pas.'
            : 'Le programme n’a pas pu être lu.'}
        </FormError>
      </Frame>
    )
  }
  const data = program.data
  const tab = onglet ?? 'grille'

  return (
    <div className="space-y-6" data-testid="programme">
      <ReturnLink
        className="-ml-2 self-start"
        fallback={
          <Link
            to="/prospecting"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeftIcon className="size-4" aria-hidden="true" />
            Prospection
          </Link>
        }
      />
      <PageHeader
        title={data.name}
        description={[
          data.product,
          data.owner ? `Responsable : ${authorName(data.owner)}` : 'Sans responsable',
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-mono text-xs text-muted-foreground">{data.short_id}</span>
        {data.status !== 'active' && (
          <Badge variant="secondary">{data.status === 'paused' ? 'En pause' : 'Archivé'}</Badge>
        )}
        {PROSPECT_TIERS.map((tier) => (
          <Link
            key={tier}
            to="/prospecting"
            search={{ programme: data.id, classe: tier }}
            className="inline-flex items-center gap-1"
          >
            <ProspectTierBadge tier={tier} />
            <span className="tabular-nums">{data.counts[tier]}</span>
          </Link>
        ))}
        <span className="text-muted-foreground">
          {data.counts.converted} converti{data.counts.converted > 1 ? 's' : ''}
        </span>
      </div>
      {data.questions.length === 0 && (
        <p
          className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm"
          data-testid="programme-sans-grille"
        >
          Ce programme n’a pas encore de grille : ses prospects arrivent tous à zéro point. Ajoutez
          les questions qui distinguent un bon prospect.
        </p>
      )}

      <Tabs
        value={tab}
        onValueChange={(next) => {
          void navigate({ search: { onglet: next as (typeof TABS)[number] }, replace: true })
        }}
      >
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="grille">Grille et seuils</TabsTrigger>
          <TabsTrigger value="meta">Meta</TabsTrigger>
          <TabsTrigger value="diffusion">Diffusion</TabsTrigger>
          <TabsTrigger value="tests">Tests A/B</TabsTrigger>
          <TabsTrigger value="regles">Règles d’arrêt</TabsTrigger>
          <TabsTrigger value="page">Page de qualification</TabsTrigger>
          <TabsTrigger value="rapport">Rapport</TabsTrigger>
          <TabsTrigger value="reglages">Réglages</TabsTrigger>
        </TabsList>
        <TabsContent value="grille">
          <Frame>
            <ProgramGrid program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="meta">
          <Frame>
            <ProgramMeta program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="diffusion">
          <Frame>
            <ProgramDelivery program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="tests">
          <Frame>
            <ProgramAbTests program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="regles">
          <Frame>
            <ProgramStopRules program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="page">
          <Frame>
            <ProgramLanding program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="rapport">
          <Frame>
            <ProgramReport program={data} />
          </Frame>
        </TabsContent>
        <TabsContent value="reglages">
          <Frame>
            <ProgramSettings program={data} />
          </Frame>
        </TabsContent>
      </Tabs>
      <ReferencedBy type="prospecting_program" id={data.id} />
    </div>
  )
}
